import React from 'react';
import PropTypes from 'prop-types';

import { get } from 'lodash';
import { Show } from '../../show';

import GridContainer from '../../layouts/components/grid-container';
import FluidContainer from '../../layouts/components/fluid-container';

import { Question, PublicTitle, Attachments } from '../components';
import AudioPlayer from '../../audio-player';
import CheckBoxGroup from '../../check-box-group';
import ImageGallery from '../../image-gallery';
import RadioGroup from '../../radio-group';
import SharedText from '../../shared-text';
import YouTubePlayer from '../../video-player/youtube';
import { calculateHighlight, exerciseDefaultProps, exercisePropTypes } from '../../../constants/exercise';
import Geogebra from '../../geogebra';
import { getCharByElementPosition } from '../../../lib/exercise';
import { Index } from './styled';
import Spacer from '../../spacer';
import Stack from '../../stack';
import MathContent from '../../math-content';

export const Component = ({ index }) => {
    return <Index>{`${getCharByElementPosition(index + 1)}.`}</Index>;
};
Component.propTypes = {
    index: PropTypes.number,
};

const MultipleChoice = ({ exercise, answers = [], onAnswer, correctionStatus }) => {
    const addMultipleChoiceAnswer = (answer) => {
        const resultAnswers = answers.filter((choice) => choice !== answer.questionId);

        if (answer.value) {
            resultAnswers.push(answer.questionId);
        }

        onAnswer(exercise.id, resultAnswers);
    };

    const options = exercise.options.map((option) => {
        const answer = answers.includes(`${option.id}`);

        return {
            ...option,
            disabled: !!correctionStatus,
            checked: !!answer,
            highlight: calculateHighlight(correctionStatus, option.correct, !!answer),
        };
    });

    return (
        <CheckBoxGroup
            Component={Component}
            name="answers"
            options={options}
            onChange={(choiceId, isChecked) => addMultipleChoiceAnswer({ questionId: choiceId, value: isChecked })}
        />
    );
};

MultipleChoice.propTypes = exercisePropTypes;

const SingleChoice = ({ exercise, answers = [], onAnswer, correctionStatus }) => {
    const addSingleChoiceChoiceAnswer = (choice) => {
        const isChecked = answers.find((answer) => answer === `${choice}`);
        onAnswer(exercise.id, isChecked ? [] : [`${choice}`]);
    };

    const options = exercise.options.map((option) => {
        const answer = answers.includes(`${option.id}`);

        return {
            ...option,
            disabled: !!correctionStatus,
            checked: !!answer,
            highlight: calculateHighlight(correctionStatus, option.correct, !!answer),
        };
    });

    return (
        <RadioGroup
            Component={Component}
            exerciseId={exercise.id}
            name="answers"
            direction="vertical"
            options={options}
            onClick={(choice) => addSingleChoiceChoiceAnswer(choice)}
        />
    );
};

SingleChoice.propTypes = exercisePropTypes;

const SingleOrMultipleChoiceSelector = ({
    exercise,
    correctionStatus,
    onPlayVideo,
    videos,
    show,
    hasAttachments = exerciseDefaultProps.hasAttachments,
    answers = exerciseDefaultProps.answers,
    onAnswer,
}) => {
    const correctAnswers = exercise.options.filter((x) => x.correct);
    const ChoicesComponent = correctAnswers.length === 1 ? SingleChoice : MultipleChoice;
    const showGrid = (hasAttachments.images || hasAttachments.video) && show.attachments;

    return (
        <>
            <Show when={show.title && exercise.publicTitle}>
                <FluidContainer paddingBottom={24}>
                    <PublicTitle>{exercise.publicTitle}</PublicTitle>
                </FluidContainer>
            </Show>

            <Show when={show.prompt && exercise.prompt}>
                <FluidContainer paddingBottom={24}>
                    <MathContent>{exercise.prompt}</MathContent>
                </FluidContainer>
            </Show>

            <Show when={show.attachments && hasAttachments.geogebra}>
                <FluidContainer paddingBottom={24}>
                    <Geogebra exerciseId={exercise.id} src={exercise.attachments.geogebra?.src} />
                </FluidContainer>
            </Show>

            <Show when={show.sharedText && hasAttachments.sharedText}>
                <FluidContainer paddingBottom={24} flexWrap="wrap">
                    <SharedText exercise={exercise} />
                </FluidContainer>
            </Show>

            {show.attachments && hasAttachments.audio && (
                <FluidContainer paddingBottom={24}>
                    <AudioPlayer source={exercise.attachments.audio?.src} />
                </FluidContainer>
            )}

            <GridContainer columns={[1, showGrid ? 2 : 1]} paddingBottom={24}>
                <Attachments show={showGrid}>
                    <Stack gap="desktop">
                        {hasAttachments.video && (
                            <YouTubePlayer
                                videoIdOrSource={exercise.attachments.video?.src}
                                onPlayVideo={() => onPlayVideo(exercise.id)}
                                showPreview={!videos.find((item) => item.exerciseId === exercise.id)}
                            />
                        )}
                        {hasAttachments.images && <ImageGallery images={get(exercise, 'attachments.images')} />}
                    </Stack>
                </Attachments>
                <div>
                    <Question>{exercise.question}</Question>
                    <Spacer size="regular" />
                    <ChoicesComponent exercise={exercise} correctionStatus={correctionStatus} answers={answers} onAnswer={onAnswer} />
                </div>
            </GridContainer>
        </>
    );
};

SingleOrMultipleChoiceSelector.propTypes = exercisePropTypes;

export default SingleOrMultipleChoiceSelector;
